import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, ElementRef } from '@angular/core';
import { AuthService } from '../auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
    baseColumns: string[] = ['date', 'company', 'detail', 'manual_check', 'reason', 'actions'];
    displayedColumns: string[] = ['date', 'company', 'detail', 'manual_check', 'reason', 'actions'];

    allVersionNumbers: number[] = [];
    dataSource: any[] = [];
    searchQuery: string = '';
    debugMode: boolean = false;

    skip: number = 0;
    limit: number = 50;
    loading: boolean = false;
    pagingState: 'next' | 'prev' | null = null;
    
    dateSortDirection: 'asc' | 'desc' = 'desc';

    stats: any = {
        total_count: 0,
        mismatch_count: 0,
        match_rate: 0
    };

    flagOptions = [
        { value: 'C-000', label: 'C-000' },
        { value: 'C-997', label: 'C-997' },
        { value: 'C-998', label: 'C-998' },
        { value: 'C-999', label: 'C-999' }
    ];

    @ViewChild('tableContainer') tableContainer!: ElementRef;
    @ViewChild('importDialog') importDialog!: TemplateRef<any>;
    @ViewChild('reviewDialog') reviewDialog!: TemplateRef<any>;

    importDataName: string = '';
    importFileName: string = '';
    selectedImportFile: File | null = null;

    selectedReviewFile: File | null = null;
    reviewFileName: string = '';

    showProgressContainer: boolean = false;
    uploadStatus: 'processing' | 'error' = 'processing';
    uploadProgress: number = 0;
    uploadStatusText: string = '';
    
    private pollingInterval: any;
    private readonly STORAGE_KEY = 'activeBatchId';
    private subscriptions: Subscription = new Subscription();

    isDeletingMap: { [key: number]: boolean } = {};

    constructor(
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private http: HttpClient
    ) { }

    ngOnInit(): void {
        const currentUser = this.authService.getCurrentUserValue();
        if (currentUser && currentUser.default_page_size) {
            this.limit = currentUser.default_page_size;
        }

        this.loadData();
        this.loadStats();

        const activeBatchId = sessionStorage.getItem(this.STORAGE_KEY);
        if (activeBatchId) {
            this.showProgressContainer = true;
            this.uploadStatus = 'processing';
            this.uploadProgress = 0;
            this.uploadStatusText = 'Resuming progress...';
            this.startPolling(activeBatchId);
        }
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.subscriptions.unsubscribe();
    }

    loadData(): void {
        this.loading = true;
        const useMismatchFilter = !this.debugMode;

        const sub = this.authService.getCreditData(this.skip, this.limit, this.searchQuery, useMismatchFilter, 'date', this.dateSortDirection).subscribe({
            next: (data: any[]) => {
                if (this.debugMode && data.length > 0) {
                    if (!data[0].logs) data[0].logs = [];
                    data[0].logs.push({
                        logic_version: 99,
                        prediction: 'NG (C-998)',
                        reason: 'Debug log'
                    });
                    data[0].original_label = 'C-997';
                }

                const versionSet = new Set<number>(this.allVersionNumbers);
                data.forEach(row => {
                    if (row.logs && Array.isArray(row.logs)) {
                        row.logs.forEach((log: any) => {
                            versionSet.add(Number(log.logic_version));
                        });
                    }
                });
                this.allVersionNumbers = Array.from(versionSet).sort((a, b) => a - b);

                this.dataSource = data.map(item => {
                    const versionList: any[] = [];
                    let hasMismatch = false;

                    this.allVersionNumbers.forEach(vNum => {
                        const log = item.logs ? item.logs.find((l: any) => Number(l.logic_version) === vNum) : null;
                        if (log) {
                            if (log.prediction !== item.original_label) {
                                hasMismatch = true;
                            }
                            versionList.push({
                                version: vNum,
                                pred: log.prediction,
                                reason: log.reason,
                                isMismatch: log.prediction !== item.original_label,
                                exists: true
                            });
                        } else {
                            versionList.push({
                                version: vNum,
                                pred: '-',
                                reason: '',
                                isMismatch: false,
                                exists: false
                            });
                        }
                    });

                    return {
                        ...item,
                        versionList: versionList,
                        isMismatch: hasMismatch,
                        editingFlag: item.confirmed_label || '',
                        editingReason: item.correction_reason || '',
                        isChanged: false,
                        isSaving: false
                    };
                });
                
                this.loading = false;
                this.pagingState = null;
                
                if (this.tableContainer) {
                    this.tableContainer.nativeElement.scrollTop = 0;
                }
            },
            error: (err: any) => {
                console.error(err);
                this.snackBar.open('Data load failed', 'Close', { duration: 3000 });
                this.loading = false;
                this.pagingState = null;
            }
        });
        this.subscriptions.add(sub);
    }

    toggleDateSort(): void {
        this.dateSortDirection = this.dateSortDirection === 'desc' ? 'asc' : 'desc';
        this.skip = 0;
        this.loadData();
    }

    loadStats(): void {
        const sub = this.authService.getCreditStats().subscribe({
            next: (res: any) => {
                this.stats.total_count = res.total_count;
                this.stats.mismatch_count = res.mismatch_count;
                
                if (res.total_count > 0) {
                    const matchCount = res.total_count - res.mismatch_count;
                    this.stats.match_rate = (matchCount / res.total_count) * 100;
                } else {
                    this.stats.match_rate = 0;
                }

                if (res.versions && Array.isArray(res.versions)) {
                    const versionSet = new Set<number>(this.allVersionNumbers);
                    res.versions.forEach((v: any) => versionSet.add(Number(v)));
                    this.allVersionNumbers = Array.from(versionSet).sort((a, b) => a - b);
                }
            },
            error: (err: any) => {
                console.error('Stats load error', err);
            }
        });
        this.subscriptions.add(sub);
    }

    onSearch(): void {
        this.skip = 0;
        this.loadData();
    }

    onAutoSave(row: any): void {
        if (!row.editingFlag) {
            if (row.editingReason && row.editingReason.trim() !== '') {
                this.snackBar.open('Label is required', 'Close', { duration: 3000 });
            }
            return;
        }

        row.isSaving = true;

        const payload = {
            confirmed_label: row.editingFlag,
            correction_reason: row.editingReason,
            review_status: 'reviewed'
        };

        const sub = this.authService.updateCreditReview(row.id, payload).subscribe({
            next: (updatedItem: any) => {
                row.confirmed_label = updatedItem.confirmed_label;
                row.correction_reason = updatedItem.correction_reason;
                row.review_status = updatedItem.review_status;
                
                row.isSaving = false;
                row.isChanged = false;
                
                this.snackBar.open('Saved', undefined, { duration: 1000 });
            },
            error: (err: any) => {
                console.error(err);
                row.isSaving = false;
                this.snackBar.open('Save failed', 'Close', { duration: 3000 });
            }
        });
        this.subscriptions.add(sub);
    }

    openImportDialog(): void {
        this.importDataName = '';
        this.importFileName = '';
        this.selectedImportFile = null;
        this.dialog.open(this.importDialog, {
            width: '500px'
        });
    }

    onImportFileSelected(event: any): void {
        const file: File = event.target.files[0];
        if (file) {
            this.selectedImportFile = file;
            this.importFileName = file.name;
        }
    }

    onUpload(): void {
        if (!this.selectedImportFile) return;

        this.dialog.closeAll();
        
        this.showProgressContainer = true;
        this.uploadStatus = 'processing';
        this.uploadProgress = 0;
        this.uploadStatusText = 'Starting upload...';

        const sub = this.authService.uploadCreditData(this.selectedImportFile, this.importDataName).subscribe({
            next: (res: any) => {
                if (res.batch_id) {
                    sessionStorage.setItem(this.STORAGE_KEY, res.batch_id);
                    this.startPolling(res.batch_id);
                } else {
                    this.snackBar.open(res.message, 'Close', { duration: 3000 });
                    this.showProgressContainer = false;
                    this.loadData();
                    this.loadStats();
                }
            },
            error: (err: any) => {
                console.error(err);
                this.uploadStatus = 'error';
                this.uploadStatusText = 'Upload failed';
                this.uploadProgress = 100;
            }
        });
        this.subscriptions.add(sub);
    }

    private startPolling(batchId: string): void {
        this.uploadStatusText = 'AI is analyzing...';
        this.stopPolling();

        this.pollingInterval = setInterval(() => {
            const sub = this.authService.getUploadStatus(batchId).subscribe({
                next: (res: any) => {
                    const total = res.total;
                    const processed = res.processed;
                    
                    if (total > 0) {
                        this.uploadProgress = (processed / total) * 100;
                        this.uploadStatusText = `AI is analyzing... (${processed} / ${total} items)`;
                    }

                    if (res.status === 'completed' || (total > 0 && processed >= total)) {
                        this.stopPolling();
                        sessionStorage.removeItem(this.STORAGE_KEY);
                        this.showProgressContainer = false;
                        this.snackBar.open('Process completed', 'Close', { duration: 3000 });
                        
                        this.skip = 0;
                        this.loadData();
                        this.loadStats();
                    }
                },
                error: (err) => {
                    console.error('Polling error', err);
                    this.handleUploadError('Error checking status');
                }
            });
            this.subscriptions.add(sub);
        }, 5000); 
    }

    private handleUploadError(message: string): void {
        this.stopPolling();
        sessionStorage.removeItem(this.STORAGE_KEY);
        this.uploadStatus = 'error';
        this.uploadStatusText = message;
        this.uploadProgress = 100;
        
        this.skip = 0;
        this.loadData();
        this.loadStats();
    }

    private stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    closeProgress(): void {
        this.showProgressContainer = false;
        sessionStorage.removeItem(this.STORAGE_KEY);
    }

    onExport(): void {
        this.loading = true;
        const useMismatchFilter = !this.debugMode;
        
        const sub = this.authService.exportCreditData(this.searchQuery, useMismatchFilter, 'date', this.dateSortDirection).subscribe({
            next: (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `credit_data_export_${new Date().getTime()}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                this.loading = false;
                this.snackBar.open('Export completed', 'Close', { duration: 3000 });
            },
            error: (err: any) => {
                console.error(err);
                this.snackBar.open('Export failed', 'Close', { duration: 3000 });
                this.loading = false;
            }
        });
        this.subscriptions.add(sub);
    }

    onNextPage(): void {
        if (this.dataSource.length < this.limit) return;
        this.pagingState = 'next';
        this.skip += this.limit;
        this.loadData();
    }

    onPrevPage(): void {
        if (this.skip === 0) return;
        this.pagingState = 'prev';
        this.skip = Math.max(0, this.skip - this.limit);
        this.loadData();
    }

    openReviewDialog(): void {
        this.reviewFileName = '';
        this.selectedReviewFile = null;
        this.dialog.open(this.reviewDialog, {
            width: '500px'
        });
    }

    onReviewFileSelected(event: any): void {
        const file: File = event.target.files[0];
        if (file) {
            this.selectedReviewFile = file;
            this.reviewFileName = file.name;
        }
    }

    onUploadReviewExcel(): void {
        if (!this.selectedReviewFile) return;

        this.loading = true;
        this.dialog.closeAll();

        const sub = this.authService.uploadCreditReviewExcel(this.selectedReviewFile).subscribe({
            next: (res: any) => {
                this.loading = false;
                this.snackBar.open(res.message, 'Close', { duration: 5000 });
                
                this.selectedReviewFile = null;
                this.reviewFileName = '';
                this.loadData();
                this.loadStats();
            },
            error: (err: any) => {
                console.error(err);
                this.loading = false;
                this.snackBar.open('Upload failed', 'Close', { duration: 3000 });
            }
        });
        this.subscriptions.add(sub);
    }

    deleteItem(row: any): void {
        const id = row.id;
        
        if (this.isDeletingMap[id]) return;
        this.isDeletingMap[id] = true;

        const sub = this.http.delete<{status: string, id: number}>(`/api/credit-data/${id}`).subscribe({
            next: () => {
                this.dataSource = this.dataSource.filter(item => item.id !== id);
                delete this.isDeletingMap[id];
                this.snackBar.open(`ID: ${id} deleted`, undefined, { duration: 2000 });
                
                this.loadStats();
            },
            error: (err: any) => {
                console.error(`[Delete] ID: ${id} failed`, err);
                delete this.isDeletingMap[id];
                this.snackBar.open(`Failed to delete (ID: ${id})`, 'Close', { duration: 3000 });
            }
        });
        this.subscriptions.add(sub);
    }
}